import React from 'react';
import type { NPCModelProps, NPCModelRegistry } from '../types';

const PolarBear = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.25, 0]} castShadow>
        <boxGeometry args={[0.35, 0.25, 0.5]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFFAFA' : '#E8E8F0'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.35, 0.28]} castShadow>
        <boxGeometry args={[0.18, 0.15, 0.18]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.32, 0.4]}>
        <boxGeometry args={[0.1, 0.08, 0.1]} />
        <meshStandardMaterial color={world === 'real' ? '#F5F5F5' : '#E0E0E8'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.33, 0.45]}>
        <sphereGeometry args={[0.025, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Ears */}
      {[-0.07, 0.07].map((x, i) => (
        <mesh key={i} position={[x, 0.44, 0.25]}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
        </mesh>
      ))}
      {/* Legs */}
      {[[-0.12, 0.18], [0.12, 0.18], [-0.12, -0.18], [0.12, -0.18]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.1, z]}>
          <cylinderGeometry args={[0.05, 0.06, 0.2, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
        </mesh>
      ))}
      {/* Eyes */}
      {[-0.05, 0.05].map((x, i) => (
        <mesh key={i} position={[x, 0.38, 0.35]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#1a1a1a" />
        </mesh>
      ))}
    </group>
  );
};

const ArcticFox = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.12, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.15, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#FFFAFA' : '#E8E8F0'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.22, 0.1]} castShadow>
        <sphereGeometry args={[0.07, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.2, 0.16]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.03, 0.06, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.19, 0.2]}>
        <sphereGeometry args={[0.012, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Ears */}
      {[-0.04, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.3, 0.08]} rotation={[0.2, 0, (i - 0.5) * 0.2]}>
          <coneGeometry args={[0.025, 0.06, 4]} />
          <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
        </mesh>
      ))}
      {/* Fluffy tail */}
      <mesh position={[0, 0.1, -0.15]} rotation={[0.8, 0, 0]}>
        <capsuleGeometry args={[0.05, 0.1, 4, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.24, 0.14]}>
          <sphereGeometry args={[0.012, 8, 8]} />
          <meshBasicMaterial color="#1a1a1a" />
        </mesh>
      ))}
    </group>
  );
};

const Penguin = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.18, 0]} castShadow>
        <capsuleGeometry args={[0.08, 0.15, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#1a1a1a' : '#2a2a3a'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Belly */}
      <mesh position={[0, 0.18, 0.04]}>
        <capsuleGeometry args={[0.06, 0.12, 6, 12]} />
        <meshStandardMaterial color={world === 'real' ? '#FFFAFA' : '#E8E8F0'} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.35, 0]} castShadow>
        <sphereGeometry args={[0.06, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#1a1a1a' : '#2a2a3a'} />
      </mesh>
      {/* Beak */}
      <mesh position={[0, 0.33, 0.06]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.015, 0.04, 4]} />
        <meshStandardMaterial color={world === 'real' ? '#FFA500' : '#b07030'} />
      </mesh>
      {/* Wings/flippers */}
      {[-0.1, 0.1].map((x, i) => (
        <mesh key={i} position={[x, 0.15, 0]} rotation={[0, 0, x > 0 ? 0.3 : -0.3]}>
          <capsuleGeometry args={[0.02, 0.1, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#1a1a1a' : '#2a2a3a'} />
        </mesh>
      ))}
      {/* Feet */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.02, 0.04]}>
          <boxGeometry args={[0.04, 0.02, 0.06]} />
          <meshStandardMaterial color={world === 'real' ? '#FFA500' : '#b07030'} />
        </mesh>
      ))}
      {/* Eyes */}
      {[-0.025, 0.025].map((x, i) => (
        <mesh key={i} position={[x, 0.37, 0.04]}>
          <sphereGeometry args={[0.01, 8, 8]} />
          <meshBasicMaterial color="#FFFFFF" />
        </mesh>
      ))}
    </group>
  );
};

const Seal = ({ world, hovered }: NPCModelProps) => {
  const highlightColor = world === 'real' ? '#FFFF00' : '#00FFFF';

  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.1, 0]} rotation={[0, 0, 0]} castShadow>
        <capsuleGeometry args={[0.1, 0.25, 8, 16]} />
        <meshStandardMaterial
          color={world === 'real' ? '#708090' : '#506070'}
          emissive={highlightColor}
          emissiveIntensity={hovered ? 0.3 : 0}
        />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.15, 0.2]} castShadow>
        <sphereGeometry args={[0.08, 10, 10]} />
        <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.13, 0.28]}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshStandardMaterial color={world === 'real' ? '#808090' : '#607080'} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.14, 0.32]}>
        <sphereGeometry args={[0.015, 6, 6]} />
        <meshStandardMaterial color="#1a1a1a" />
      </mesh>
      {/* Whiskers */}
      {[-0.03, 0, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.12, 0.3]} rotation={[0, 0, (i - 1) * 0.3]}>
          <cylinderGeometry args={[0.002, 0.001, 0.04, 4]} />
          <meshStandardMaterial color="#2a2a2a" />
        </mesh>
      ))}
      {/* Flippers */}
      {[-0.12, 0.12].map((x, i) => (
        <mesh key={i} position={[x, 0.05, 0.05]} rotation={[0, 0, x > 0 ? 0.8 : -0.8]}>
          <capsuleGeometry args={[0.025, 0.08, 4, 6]} />
          <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} />
        </mesh>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.08, -0.2]} rotation={[0.5, 0, 0]}>
        <coneGeometry args={[0.06, 0.1, 6]} />
        <meshStandardMaterial color={world === 'real' ? '#708090' : '#506070'} />
      </mesh>
      {/* Eyes */}
      {[-0.03, 0.03].map((x, i) => (
        <mesh key={i} position={[x, 0.18, 0.25]}>
          <sphereGeometry args={[0.015, 8, 8]} />
          <meshBasicMaterial color="#1a1a1a" />
        </mesh>
      ))}
    </group>
  );
};

export const iceAnimals: NPCModelRegistry = {
  polar_bear: PolarBear,
  arctic_fox: ArcticFox,
  penguin: Penguin,
  seal: Seal,
};
